const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];
const DEFAULT_DURATION_DAYS = 30;

const parseDate = (value) => {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const parts = value.split('-');
  if (parts.length !== 3) {
    return null;
  }

  const [year, month, day] = parts.map((part) => Number(part));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }

  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
};

const formatDateInput = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (date, days = 1) => {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const getDefaultDateRange = () => {
  const today = new Date();
  const defaultEnd = addDays(today, DEFAULT_DURATION_DAYS);
  return {
    start: formatDateInput(today),
    end: formatDateInput(defaultEnd),
  };
};

const buildDateRange = (start, end) => {
  const startDate = parseDate(start);
  const endDate = parseDate(end);
  if (!startDate || !endDate || startDate > endDate) {
    return [];
  }

  const range = [];
  let cursor = new Date(startDate.getTime());

  while (cursor <= endDate) {
    range.push(formatDateInput(cursor));
    cursor = addDays(cursor, 1);
  }

  return range;
};

const formatDateLabel = (value) => {
  const date = parseDate(value);
  if (!date) {
    return '-';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const weekday = WEEKDAY_LABELS[date.getDay()];
  return `${year}/${month}/${day} (${weekday})`;
};

const formatShortDate = (value) => {
  const date = parseDate(value);
  if (!date) {
    return '--/--';
  }

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}`;
};

const formatMinutes = (minutes) => {
  const value = Number(minutes) || 0;
  const hours = Math.floor(value / 60);
  const remain = value % 60;
  if (hours && remain) {
    return `${hours}時間${remain}分`;
  }
  if (hours) {
    return `${hours}時間`;
  }
  return `${remain}分`;
};

const formatHours = (minutes, fractionDigits = 1) => {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return Number(0).toFixed(fractionDigits);
  }
  const hours = minutes / 60;
  return hours.toFixed(fractionDigits);
};

const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const schedulePlan = (texts, startDate, targetEndDate) => {
  if (!texts.length || !startDate || !targetEndDate) {
    return {
      items: [],
      dailyMinutes: 0,
      totalMinutes: 0,
      weeklyMinutes: 0,
      planned_end_date: startDate || '',
      target_end_date: targetEndDate || '',
      dateRange: startDate ? [startDate] : [],
      dayCount: 0,
      textCount: 0,
      weekCount: 0,
    };
  }

  const start = parseDate(startDate);
  const target = parseDate(targetEndDate);
  if (!start || !target || start > target) {
    return {
      items: [],
      dailyMinutes: 0,
      totalMinutes: 0,
      weeklyMinutes: 0,
      planned_end_date: startDate || '',
      target_end_date: targetEndDate || '',
      dateRange: [],
      dayCount: 0,
      textCount: 0,
      weekCount: 0,
    };
  }

  const dateRange = buildDateRange(startDate, targetEndDate);
  if (!dateRange.length) {
    return {
      items: [],
      dailyMinutes: 0,
      totalMinutes: 0,
      weeklyMinutes: 0,
      planned_end_date: '',
      target_end_date: targetEndDate || '',
      dateRange: [],
      dayCount: 0,
      textCount: 0,
      weekCount: 0,
    };
  }

  const dayCount = dateRange.length;
  const weekCount = Math.ceil(dayCount / 7);
  const totalMinutes = texts.reduce((sum, text) => {
    const value = Number(text.standard_minutes) || 0;
    return value > 0 ? sum + value : sum;
  }, 0);

  if (totalMinutes <= 0) {
    return {
      items: [],
      dailyMinutes: 0,
      weeklyMinutes: 0,
      totalMinutes: 0,
      planned_end_date: startDate,
      target_end_date: targetEndDate,
      dateRange,
      dayCount,
      textCount: 0,
      weekCount,
    };
  }

  const dailyMinutes = Math.max(1, Math.ceil(totalMinutes / dayCount));
  const weeklyMinutes = dailyMinutes * 7;

  const dayCapacities = Array.from({ length: dayCount }, () => dailyMinutes);
  let dayPointer = 0;
  let actualEndIndex = 0;
  const items = [];
  let textCount = 0;

  texts.forEach((text) => {
    const minutes = Number(text.standard_minutes) || 0;
    if (minutes <= 0) {
      return;
    }

    textCount += 1;
    let minutesLeft = minutes;
    let textStartIndex = null;
    let textEndIndex = null;

    while (minutesLeft > 0) {
      if (dayPointer >= dayCapacities.length) {
        dayCapacities.push(dailyMinutes);
        const lastDate = parseDate(dateRange[dateRange.length - 1]);
        dateRange.push(formatDateInput(addDays(lastDate, 1)));
      }

      if (dayCapacities[dayPointer] <= 0) {
        dayPointer += 1;
        continue;
      }

      const allocation = Math.min(dayCapacities[dayPointer], minutesLeft);
      minutesLeft -= allocation;
      dayCapacities[dayPointer] -= allocation;

      if (textStartIndex === null) {
        textStartIndex = dayPointer;
      }

      textEndIndex = dayPointer;

      if (dayCapacities[dayPointer] <= 0) {
        dayPointer += 1;
      }
    }

    if (textStartIndex !== null && textEndIndex !== null) {
      actualEndIndex = Math.max(actualEndIndex, textEndIndex);

      items.push({
        text_id: text.id,
        text_name: text.text_name,
        planned_start_date: dateRange[textStartIndex],
        planned_end_date: dateRange[textEndIndex],
        allocated_minutes: minutes,
        sort_order: items.length + 1,
      });
    }
  });

  const actualPlannedEndDate = items.length
    ? items[items.length - 1].planned_end_date
    : startDate;

  return {
    items,
    dailyMinutes,
    totalMinutes,
    weeklyMinutes,
    planned_end_date: actualPlannedEndDate,
    target_end_date: targetEndDate,
    dateRange,
    dayCount: dateRange.length,
    textCount,
    weekCount,
  };
};

const extractErrorMessage = (error) => {
  if (!error) {
    return '予期せぬエラーが発生しました。';
  }

  if (error.response && error.response.data) {
    if (typeof error.response.data === 'string') {
      return error.response.data;
    }
    if (error.response.data.message) {
      return error.response.data.message;
    }
  }

  if (error.message) {
    return error.message;
  }

  return '予期せぬエラーが発生しました。';
};

class LearningPlanApp {
  constructor() {
    this.root = document.getElementById('learning-plan-app');
    if (!this.root) {
      return;
    }

    this.optionsUrl = this.root.dataset.optionsUrl;
    this.lookupUrl = this.root.dataset.lookupUrl;
    this.storeUrl = this.root.dataset.storeUrl;
    this.rescheduleUrlTemplate = this.root.dataset.rescheduleUrl;
    this.showUrlTemplate = this.root.dataset.showUrl;

    const defaultRange = getDefaultDateRange();
    this.state = {
      loading: true,
      submitting: false,
      error: null,
      success: null,
      users: [],
      courses: [],
      selectedUserId: '',
      selectedCourseId: '',
      startDate: defaultRange.start,
      endDate: defaultRange.end,
      planLoading: false,
      activePlan: null,
      archivedPlans: [],
    };

    this.fetchOptions();
  }

  async fetchOptions() {
    this.setState({ loading: true, error: null });
    const optionsUrl = this.optionsUrl;

    try {
      const response = await window.axios.get(optionsUrl);
      const { users = [], courses = [] } = response.data || {};

      const selectedUserId = this.state.selectedUserId || (users[0] ? String(users[0].id) : '');
      const selectedCourseId = this.state.selectedCourseId || (courses[0] ? String(courses[0].id) : '');

      this.setState({
        loading: false,
        error: null,
        users,
        courses,
        selectedUserId,
        selectedCourseId,
      }, () => {
        this.fetchCurrentPlan(selectedUserId, selectedCourseId);
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: extractErrorMessage(error),
      });
    }
  }

  async fetchCurrentPlan(userId, courseId) {
    if (!userId || !courseId || !this.lookupUrl) {
      this.setState({ activePlan: null, archivedPlans: [] });
      return;
    }

    this.setState({ planLoading: true });

    try {
      const response = await window.axios.get(`${this.lookupUrl}?user_id=${userId}&course_id=${courseId}`);
      const { active_plan: activePlan = null, archived_plans: archivedPlans = [] } = response.data || {};

      let startDate = this.state.startDate;
      let endDate = this.state.endDate;

      if (activePlan) {
        const todayRange = getDefaultDateRange();
        startDate = todayRange.start;
        endDate = activePlan.planned_end_date || todayRange.end;
      }

      this.setState({
        activePlan,
        archivedPlans,
        planLoading: false,
        startDate,
        endDate,
      });
    } catch (error) {
      this.setState({
        planLoading: false,
        error: extractErrorMessage(error),
      });
    }
  }
  setState(patch, callback) {
    this.state = { ...this.state, ...patch };
    this.render();
    if (typeof callback === 'function') {
      callback();
    }
  }

  getSelectedCourse() {
    const courseId = Number(this.state.selectedCourseId);
    if (!courseId) {
      return null;
    }
    return this.state.courses.find((course) => course.id === courseId) || null;
  }

  computePreview() {
    const course = this.getSelectedCourse();
    if (!course) {
      return null;
    }

    const startDate = this.state.startDate;
    const endDate = this.state.endDate;
    if (!startDate || !endDate) {
      return null;
    }

    return schedulePlan(course.texts, startDate, endDate);
  }

  buildWeeklyLoad(weeklyMinutes) {
    if (!weeklyMinutes || weeklyMinutes <= 0) {
      return {
        level: 'muted',
        label: '未計算',
        message: '学習開始日と目標完了日を設定すると必要時間を表示します。',
      };
    }

    if (weeklyMinutes >= 1200) {
      return {
        level: 'danger',
        label: '危険水位',
        message: `週あたり約${formatHours(weeklyMinutes)}時間の学習が必要です。計画の見直しを強く推奨します。`,
      };
    }

    if (weeklyMinutes >= 900) {
      return {
        level: 'warning',
        label: '注意',
        message: `週あたり約${formatHours(weeklyMinutes)}時間の学習が必要です。無理のない計画か確認してください。`,
      };
    }

    return {
      level: 'safe',
      label: '適正',
      message: `週あたり約${formatHours(weeklyMinutes)}時間の学習ペースです。`,
    };
  }

  handleUserChange(event) {
    const nextUserId = event.target.value;
    this.setState({ selectedUserId: nextUserId }, () => {
      this.fetchCurrentPlan(nextUserId, this.state.selectedCourseId);
    });
  }

  handleCourseChange(event) {
    const value = event.target.value;
    this.setState({ selectedCourseId: value }, () => {
      this.fetchCurrentPlan(this.state.selectedUserId, value);
    });
  }

  handleStartDateChange(event) {
    const value = event.target.value;
    let endDate = this.state.endDate;

    if (endDate && value && parseDate(value) && parseDate(endDate) && parseDate(value) > parseDate(endDate)) {
      endDate = value;
    }

    this.setState({ startDate: value, endDate });
  }

  handleEndDateChange(event) {
    const value = event.target.value;
    if (!value) {
      this.setState({ endDate: value });
      return;
    }

    const startDate = parseDate(this.state.startDate);
    const endDate = parseDate(value);

    if (startDate && endDate && endDate < startDate) {
      this.setState({ endDate: this.state.startDate });
      return;
    }

    this.setState({ endDate: value });
  }

  async handleSubmit() {
    if (this.state.submitting) {
      return;
    }

    const course = this.getSelectedCourse();
    if (!course) {
      this.setState({ error: 'コースを選択してください。' });
      return;
    }

    if (!this.state.selectedUserId) {
      this.setState({ error: '受講者を選択してください。' });
      return;
    }

    const isReschedule = Boolean(this.state.activePlan);

    const payload = isReschedule
      ? {
          planned_start_date: this.state.startDate,
          planned_end_date: this.state.endDate,
        }
      : {
          user_id: Number(this.state.selectedUserId),
          course_id: Number(this.state.selectedCourseId),
          planned_start_date: this.state.startDate,
          planned_end_date: this.state.endDate,
        };

    this.setState({ submitting: true, error: null, success: null });

    try {
      const response = isReschedule
        ? await window.axios.post(
            this.rescheduleUrlTemplate.replace('__ID__', this.state.activePlan.id),
            payload,
          )
        : await window.axios.post(this.storeUrl, payload);

      const message = isReschedule ? '学習計画をリスケジュールしました。' : '学習計画を作成しました。';
      const planData = response.data?.plan || null;

      this.setState({
        submitting: false,
        success: message,
        activePlan: planData,
      }, () => {
        this.fetchCurrentPlan(this.state.selectedUserId, this.state.selectedCourseId);
      });
    } catch (error) {
      this.setState({
        submitting: false,
        error: extractErrorMessage(error),
      });
    }
  }

  renderLoading() {
    this.root.innerHTML = '<div class="text-center py-5 text-muted">読み込み中...</div>';
  }

  render() {
    if (this.state.loading) {
      this.renderLoading();
      return;
    }

    const preview = this.computePreview();
    const hasActivePlan = Boolean(this.state.activePlan);
    const submitLabel = this.state.submitting
      ? (hasActivePlan ? 'リスケ中...' : '作成中...')
      : (hasActivePlan ? 'リスケジュールを作成' : '学習計画を作成');

    const userOptions = this.state.users.map((user) => (
      `<option value="${user.id}" ${String(user.id) === String(this.state.selectedUserId) ? 'selected' : ''}>${escapeHtml(user.name)}</option>`
    )).join('');

    const courseOptions = this.state.courses.map((courseItem) => (
      `<option value="${courseItem.id}" ${String(courseItem.id) === String(this.state.selectedCourseId) ? 'selected' : ''}>${escapeHtml(courseItem.course_name)}</option>`
    )).join('');

    let currentPlanSection = '';
    if (this.state.planLoading) {
      currentPlanSection = '<div class="learning-plan__current learning-plan__current--loading">現在の学習計画を読み込み中...</div>';
    } else if (this.state.activePlan) {
      const plan = this.state.activePlan;
      currentPlanSection = `
        <div class="learning-plan__current">
          <div class="learning-plan__current-header">
            <div class="learning-plan__current-title">最新の学習計画 (第${escapeHtml(String(plan.version))}版)</div>
            <div class="learning-plan__current-meta">${escapeHtml(formatDateLabel(plan.planned_start_date))} 〜 ${escapeHtml(formatDateLabel(plan.planned_end_date))}</div>
          </div>
          <div class="learning-plan__current-body">
            <div class="learning-plan__current-metric">週あたり: <strong>${escapeHtml(formatMinutes(plan.weekly_minutes))}</strong></div>
            <div class="learning-plan__current-metric">日あたり: <strong>${escapeHtml(formatMinutes(plan.daily_minutes))}</strong></div>
            <div class="learning-plan__current-sub">学習期間 ${escapeHtml(String(plan.week_count ?? 0))} 週 / テキスト ${escapeHtml(String(plan.text_count ?? 0))} 件</div>
          </div>
        </div>
      `;
    }

    const historySection = this.state.archivedPlans && this.state.archivedPlans.length
      ? `
        <div class="learning-plan__history">
          <div class="learning-plan__history-title">過去の学習計画</div>
          <ul class="learning-plan__history-list">
            ${this.state.archivedPlans.map((plan) => `
              <li class="learning-plan__history-item">
                <div class="learning-plan__history-version">第${escapeHtml(String(plan.version))}版</div>
                <div class="learning-plan__history-period">${escapeHtml(formatDateLabel(plan.planned_start_date))} 〜 ${escapeHtml(formatDateLabel(plan.planned_end_date))}</div>
              </li>
            `).join('')}
          </ul>
        </div>
      `
      : '';

    let metaSection = '<div class="learning-plan__empty">学習期間のプレビューはコースと学習期間を設定すると表示されます。</div>';
    if (preview) {
      if (preview.totalMinutes > 0) {
        const weeklyLoad = this.buildWeeklyLoad(preview.weeklyMinutes);
        const badgeClass = `learning-plan__badge learning-plan__badge--${weeklyLoad.level}`;
        const noteClass = `learning-plan__meta-note learning-plan__meta-note--${weeklyLoad.level}`;

        metaSection = `
          <div class="learning-plan__meta">
            <div class="learning-plan__meta-item">
              <div class="learning-plan__meta-label">目標完了日</div>
              <div class="learning-plan__meta-value">
                <span class="learning-plan__meta-number">${escapeHtml(formatDateLabel(preview.target_end_date))}</span>
              </div>
            </div>
            <div class="learning-plan__meta-item">
              <div class="learning-plan__meta-label">学習期間</div>
              <div class="learning-plan__meta-value">
                <span class="learning-plan__meta-sub">（${escapeHtml(String(preview.weekCount))} 週）</span>
                <span class="learning-plan__meta-number">${escapeHtml(String(preview.dayCount))} 日</span>
              </div>
              <div class="learning-plan__meta-note learning-plan__meta-note--muted">開始日から目標日までの合計日数です。</div>
            </div>
            <div class="learning-plan__meta-item learning-plan__meta-item--load">
              <div class="learning-plan__meta-label">週の必要学習時間</div>
              <div class="learning-plan__meta-value">
                <span class="${badgeClass}">${escapeHtml(weeklyLoad.label)}</span>
                <span class="learning-plan__meta-number">${escapeHtml(formatMinutes(preview.weeklyMinutes))}</span>
              </div>
              <div class="${noteClass}">${escapeHtml(weeklyLoad.message)}</div>
            </div>
            <div class="learning-plan__meta-item">
              <div class="learning-plan__meta-label">日の必要学習時間</div>
              <div class="learning-plan__meta-value">
                <span class="learning-plan__meta-number">${escapeHtml(formatMinutes(preview.dailyMinutes))}</span>
              </div>
              <div class="learning-plan__meta-note learning-plan__meta-note--muted">約${escapeHtml(formatHours(preview.dailyMinutes))}時間 / 日のペースです。</div>
            </div>
          </div>
        `;
      } else {
        metaSection = '<div class="learning-plan__empty">テキストの基準時間が設定されていません。「学習基準時間設定」で登録してください。</div>';
      }
    }

    const alerts = `
      ${this.state.error ? `<div class="learning-plan__error">${escapeHtml(this.state.error)}</div>` : ''}
      ${this.state.success ? `<div class="learning-plan__success">${escapeHtml(this.state.success)}</div>` : ''}
    `;

    this.root.innerHTML = `
          ${alerts}
          <div class="learning-plan">
            <div class="learning-plan__panel">
              <div class="learning-plan__panel-header">
                <h4 class="learning-plan__panel-title">学習計画を作成</h4>
              </div>
              <div class="learning-plan__field-group">
                <label class="form-label learning-plan__field learning-plan__field--select">受講者
                  <select class="form-select" data-action="change-user">
                    <option value="">選択してください</option>
                ${userOptions}
              </select>
            </label>
            <label class="form-label learning-plan__field learning-plan__field--select">コース
                  <select class="form-select" data-action="change-course">
                    <option value="">選択してください</option>
                    ${courseOptions}
                  </select>
                </label>
                <label class="form-label learning-plan__field learning-plan__field--date">学習開始日
                  <input type="date" class="form-control" value="${this.state.startDate}" data-action="change-start-date" />
                </label>
                <label class="form-label learning-plan__field learning-plan__field--date">目標完了日
                  <input type="date" class="form-control" value="${this.state.endDate}" data-action="change-end-date" />
                </label>
              </div>
              ${currentPlanSection}
              <div class="learning-plan__actions">
                <button type="button" class="learning-plan__button learning-plan__button--primary" data-action="submit-plan" ${this.state.submitting ? 'disabled' : ''}>
                  ${submitLabel}
                </button>
              </div>
              ${historySection}
          ${metaSection}
        </div>
      </div>
    `;

    this.attachEvents();
  }

  attachEvents() {
    const userSelect = this.root.querySelector('[data-action="change-user"]');
    if (userSelect) {
      userSelect.addEventListener('change', (event) => this.handleUserChange(event));
    }

    const courseSelect = this.root.querySelector('[data-action="change-course"]');
    if (courseSelect) {
      courseSelect.addEventListener('change', (event) => this.handleCourseChange(event));
    }

    const startInput = this.root.querySelector('[data-action="change-start-date"]');
    if (startInput) {
      startInput.addEventListener('change', (event) => this.handleStartDateChange(event));
    }

    const endInput = this.root.querySelector('[data-action="change-end-date"]');
    if (endInput) {
      endInput.addEventListener('change', (event) => this.handleEndDateChange(event));
    }


    const submitButton = this.root.querySelector('[data-action="submit-plan"]');
    if (submitButton) {
      submitButton.addEventListener('click', () => this.handleSubmit());
    }
  }
}

export default LearningPlanApp;
